import { useCallback, useEffect, useState } from "react";
import { InputNumber, Select, Form, Input, Upload, Button, Descriptions } from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons"

import { uploadFile } from "../../api/file";

import { EquipmentType, EquipmentStatus, RateBy } from "../../lib/constants";
import { compressImage, formatDate } from "../../lib/utils";

import AttachmentModal from "../shared/AttachmentModal";

export default function EquipmentForm({ onFormInstanceReady, formClassName }) {
    const [form] = Form.useForm();
    const [showAttachmentsModal, setShowAttachementsModal] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [viewAttachments, setViewAttachments] = useState([]);

    const openAttachmentsModal = () => {
        setShowAttachementsModal(true);
    }

    const closeAttachmentsModal = () => {
        setShowAttachementsModal(false);
    }

    useEffect(() => {
        onFormInstanceReady(form);
        setAttachments(form.getFieldValue("attachments") || [])
    }, [form, onFormInstanceReady]);

    const updateViewAttachments = useCallback(() => {
        const header = [
            {
                key: "1",
                label: "Attachments",
                labelStyle: { border: "0" },
                contentStyle: { backgroundColor: "rgba(0, 0, 0, 0.02)" },
                span: 3,
            },
        ];

        attachments.map((attachment, index) => {
            let expireAt = attachment.fileAttachment.expireAt;
            if (typeof expireAt === "string") {
                expireAt = formatDate(new Date(expireAt));
            } else {
                expireAt = attachment.fileAttachment.expireAt.format("YYYY-MM-DD");
            }

            return header.push(
                {
                    key: `${attachment.id + index + 1}`,
                    label: "Document Name",
                    children: attachment.fileAttachment.name
                },
                {
                    key: `${attachment.id + index + 2}`,
                    label: "Expiry Date",
                    children: expireAt,
                },
                {
                    key: `${attachment.id + index + 3}`,
                    label: "Document",
                    children: attachment.fileAttachment.file.name
                })
        });
        setViewAttachments(header);
    }, [attachments])

    useEffect(() => {
        updateViewAttachments();
    }, [updateViewAttachments, attachments]);

    const uploadProps = {
        beforeUpload: async (file) => {
            const compressedFile = await compressImage(file);
            return compressedFile;
        },
        customRequest: async ({ file, onSuccess, onError }) => {
            try {
                const res = await uploadFile(file, { entity: "equipment" });
                onSuccess(res);
            } catch (err) {
                onError(err);
            }
        },
        listType: "picture-card",
        multiple: false,
        maxCount: 4,
        accept: "image/*",
    }

    return (
        <div>
            <Form form={form} layout="vertical" className={formClassName} >
                <div className="grid grid-cols-2 gap-x-4">
                    <Form.Item
                        name={"avatars"}
                        label="Equipment Images"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                        className="col-span-2"
                    >
                        <Upload {...uploadProps} >
                            <PlusOutlined className="mr-1" />
                            Upload
                        </Upload>
                    </Form.Item>

                    <Form.Item
                        name={"name"}
                        label="Equipment Name"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name={"nickName"}
                        label="Nickname"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name={"make"}
                        label="Make"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name={"model"}
                        label="Model / Size"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name={"status"}
                        label="Status"
                        rules={[{ required: true }]}
                    >
                        <Select
                            options={Object.keys(EquipmentStatus).map((status) => {
                                return {
                                    value: EquipmentStatus[status],
                                    label: status
                                }
                            })}
                        />
                    </Form.Item>
                    <Form.Item
                        name={"type"}
                        label="Equipment Type"
                        rules={[{ required: true }]}
                    >
                        <Select
                            options={Object.keys(EquipmentType).map((type) => {
                                return {
                                    value: EquipmentType[type],
                                    label: type
                                }
                            })}
                        />
                    </Form.Item>
                    <Form.Item
                        name={"rate"}
                        label="Rate"
                    >
                        <InputNumber addonBefore="AU$" defaultValue={0.00} min={0} step={"1.00"} className="w-full" />
                    </Form.Item>
                    <Form.Item
                        name={"rateBy"}
                        label="Rate by"
                    >
                        <Select options={Object.keys(RateBy).map((rate) => {
                            return {
                                value: RateBy[rate],
                                label: rate
                            }
                        })} />
                    </Form.Item>
                    <Form.Item
                        name={"description"}
                        label="Description"
                        className="col-span-2"
                    >
                        <Input.TextArea />
                    </Form.Item>


                    <Form.Item label="Attachments" value={attachments} name="attachments">
                        <Button className="px-2" onClick={openAttachmentsModal} icon={<UploadOutlined />}> Click to Upload</Button>

                    </Form.Item>


                </div>
            </Form>
            <AttachmentModal
                open={showAttachmentsModal}
                close={closeAttachmentsModal}
                entity={"equipment"}
                onSave={(values) => {
                    form.setFieldValue("attachments", values)
                    setAttachments(values)
                }}
                attachments={attachments}
            />

            <Descriptions bordered items={viewAttachments} className="pb-6" />

        </div>)

}
